import React, { useState } from 'react'
import { Content, LinksList, getAdviceExclusionsTypes } from './Advice';
import ReactJson from 'react-json-view';
import { generateRegistryRequestLink } from '../../../classes/lib-common';
import ComponentpediaLink from '../componentpedia';
import { useSelector } from 'react-redux';
import Chai from './Chai';
import ApproveLibrary from "./ApproveLibrary"
import DOMPurify from 'dompurify'
const { convertMarkdownToHtml} = window

// ReportDataProcessor keys a CVE-less advice by advice.id and ASSIGNS rather
// than pushes (ReportDataProcessor.js:131), so a policy violation arrives here
// with exactly one affected component - the last one processed. Reading [0] is
// therefore reading the only entry there is, not the first of many.
const approvableVersion = (advice) => {
    const components = advice.affectedComponents || []
    if (!components.length) return null
    const component = components[0]
    return component.dependency ? component.dependency.version : null
}
const Description = ({display, advice}) => {
    if (!advice.description) advice.description = "";
	const severityClass = "level-" +  advice.severity.toLowerCase()
	const purifiedDescription = convertMarkdownToHtml(advice.description, DOMPurify)
    const severity = advice.severity === "NONE" ? "INFO" : advice.severity
    return display ? <div className='pl-4 clearfix' data-tab-content={"description"}>
            {/* Floated first so it shares the top line of the panel, as the mockup has it. */}
            <div className="float-right">
                <ApproveLibrary advice={advice} version={approvableVersion(advice)} />
            </div>
			<span className="advice-description" dangerouslySetInnerHTML={{ __html: purifiedDescription }}></span><p className="threat-description mt-5">{advice.type && advice.type.toLowerCase() === "security" ? <React.Fragment>THREAT: <strong className={severityClass}>{severity}</strong> </React.Fragment> : "SUGGESTION"}</p>
    </div>: ""
}
const AffectedComponents = ({display, components, advice, findDependencyInTree}) => {
    const hiddenLanguages = useSelector((state) => state.languagesFilter.toggledLanguages)
    return display ? <div className='w-100 mr-2 pl-4' data-tab-content={"affected-components"}>
        <table className='w-100'>
            <thead>
                <tr>
                    <th width={"35%"} className='p-2'>Name</th>
                    <th width={"15%"} className='p-2 text-center'>Language</th>
                    <th width={"15%"} className='p-2 text-center'>Last Patch</th>
                    <th width={"15%"} className='p-2 text-center'>Last Minor</th>
                    <th width={"15%"} className='p-2 text-center'>Last Major</th>
                </tr>
            </thead>
            <tbody>
                {components.map((component, i) => {
                    const {library, version} = component.dependency
                    const url = generateRegistryRequestLink(library.language,library.name, version)
                    if(hiddenLanguages.includes(library.language)) return null
                    return(
                        <tr key={`${i}-tr-${library.name}:${version}-${advice.id}-${i}`} className='affected-component' >
                            <td className='p-2 d-block fs-2' >
                                <div className="text-left text-link mr-2" 
                                    data-link={"dependency"}
                                    tabIndex={0} 
                                    onClick={()=>{findDependencyInTree(library.language,library.name+" "+version)}} 
                                    onKeyUp={(e)=>{findDependencyInTree(library.language,library.name+" "+version, {key: e.key})}}
                                >{library.name}:{version}</div>
                                <ComponentpediaLink language={library.language} name={library.name} version={version}/>
                                <a href={url} data-link={"registry"} target='_blank' rel="noopener noreferrer"><i className="ml-2 mr-2 fa fa-external-link pointer" ></i></a>
                            </td>
                            <td className='p-2 text-center'>{library.language}</td>
                            <td className='p-2 text-center'>{component.safeVersions.latestPatch || "-"}</td>
                            <td className='p-2 text-center'>{component.safeVersions.latestMinor || "-"}</td>
                            <td className='p-2 text-center'>{component.safeVersions.latestMajor || "-"}</td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    </div>: null
}
const SysAdminPanel = ({ display, advice }) => {
	return (
		display ?
			<div role="tabpanel" className="pl-4" data-tab-content={"sysadmin"}>
				<ReactJson src={advice}/>
			</div>
		: null
	)
}
const Actions = ({display, advice, shouldShow, newMitigations}) => {
    const exclude = (reason) => {
        const mitigationData = {
            domain: "security",
            adviceContent: {
                uuid: advice.id
            },
            title: "Exclude - issue:",
            hideLibraryMitigation: true,
            cveContent: {cve: advice.prominentIdentifier},
            content: {reason}
        }
        newMitigations("mitigated", mitigationData, shouldShow.globalExclusion)
    }
    return display ? <div className='d-flex flex-column pl-4' data-tab-content={"actions"}>
                <div>
                    <button className="btn btn-primary btn-action issue-mitigate"
                        title="This action is available only to authorized roles"
                        onClick={() => { exclude("mitigated") }}
                    >
                        Mitigate
                    </button> Provide a mitigation for this issue

                    <br />

                    <button className="btn btn-primary btn-action issue-mitigate"
                        title="This action is available only to authorized roles"
                        onClick={() => { exclude("unapplicable") }}
                    >
                        Unapplicable
                    </button> Mark this issue as not applicable

                    <br />

                    <button className="btn btn-primary btn-action issue-mitigate"
                        onClick={() => { exclude("invalid") }}>
                        Report
                    </button> Report this issue as incorrect
                </div>
			</div>: null
}
const MoreInfo = ({display, advice}) => {
    const allIds = [...new Set([
        ...(advice.cve && advice.cve !== "N/A" ? [advice.cve] : []),
        ...(advice.identifiers || []).map(id => id.toUpperCase())
    ])].filter(id => ['CVE', 'GHSA', 'OSV', 'MAL'].includes(id.split('-')[0]))
    return display ? <div className='' data-tab-content={"more-info"}>
        {allIds.length > 0 &&
            <div className="mb-2">
                <strong>Identifiers:</strong>
                <ul>{allIds.map(identifier => <li key={identifier}>{identifier}</li>)}</ul>
            </div>
        }
        {advice.links.length > 0 &&
            <div className="mb-2">
                <strong>Links:</strong>
                <LinksList links={advice.links} id={advice.id}/>
            </div>
        }
    </div>: null
}
const LearnMore = ({display, advice}) => {
    const [advisoriesSCWContent, setAdvisoriesSCWContent] = useState({ description: "" })
    const [fetchedBefore, setFetchedBefore] = useState(false)
    const fetchCWEInfo = async (cwe) => {
        cwe = cwe.replace("CWE-", "");
        try {
            const accessToken = window.AuthUtils.getTokenFromLocalStorage()
            const request = await fetch("/api/v1/vulns/cwe/" + cwe, {
                headers: {
                    "Authorization": "Bearer " + accessToken
                }
            })
            if (request.status !== 200) throw new Error("Could not get CWE info from Meterian")
            const cweInfo = await request.json()
            setAdvisoriesSCWContent({ ...cweInfo, scw: false })
        }
        catch (e) {
            setAdvisoriesSCWContent({ scw: false, description: "Could not get additional information about this vulnerability" })
        }
    }
    const fetchSCWContentForAdvice = async (cwe, language) => {
        cwe = cwe.replace("CWE-", "");
        if (advisoriesSCWContent.description.length) return;
        try {
            const request = await fetch("https://integration-api.securecodewarrior.com/api/v1/trial?Id=meterian&MappingList=cwe&MappingKey=" + cwe + "&LanguageKey=" + language)
            if (request.status !== 200) throw new Error("Request not accepted");
            const adviceContent = await request.json();
            setAdvisoriesSCWContent({ ...adviceContent, scw: true })
        }
        catch (e) {
            await fetchCWEInfo(cwe)
        }
    }
    if(display && !fetchedBefore) {
        try {
            fetchSCWContentForAdvice(advice.cwe, advice.library.language)
            setFetchedBefore(true)
        } catch(e) {
            console.error(e)
        }
    }
    return display ? <div className="pl-4" data-tab-content={"learn-more"}><Content advice={advice} advisoriesSCWContent={advisoriesSCWContent}/></div>
    : null
}
const Exclusion = ({advice, shouldShow, display, removeMitigations}) => {
    let exclusionElement;
    if(shouldShow.onlyExclusion) {
        exclusionElement = {
            domain: "security",
            uuid: advice.exclusions[0].id,
            library: advice.library.name,
            language: advice.library.language,
            cve: advice.cve && advice.cve !== "N/A" ? advice.cve : null,
            choices: getAdviceExclusionsTypes(advice.exclusions)
            
        }
    }
    return shouldShow.onlyExclusion && display ? <div className='p-3 pl-4' data-tab-content={"mitigation"}>
        Reason: <strong>{advice.exclusions[0].reason}</strong>
		{shouldShow.actions ?
			<button className="btn btn-primary btn-action issue-demitigate"
				title={advice.exclusions[0].readonly ?
					"This exclusion is embedded in the codebase on the '.meterian' file, and therefore it cannot be removed from this UI."
					: "This action is available only to authorized roles"
				}
				disabled={advice.exclusions[0].readonly}
				onClick={() => removeMitigations(exclusionElement, shouldShow.globalExclusion)}
                >
				Remove
			</button> :
			""
		}
		<p>{advice.exclusions[0].description}</p>
    </div>: null
}
const Tabs = ({selectedTab, setSelectedTab, shouldShow, advice}) => {
    const  { hasChaiFeature } = useSelector((state) => state.projectInfo.titleState)
    const severity = advice.severity === "NONE" ? "INFO" : advice.severity
    const severityClass = "level-" + (shouldShow.onlyExclusion ? "severity" : advice.severity.toLowerCase())
    // hasChaiFeature && shouldShow.chaiEnabled 
    return <div className='nav-tabs-container'>
        <ul className='nav nav-tabs position-relative'>
            {shouldShow.onlyExclusion ? 
                <li className={`nav-item`}>
                    <a className={`nav-link ${selectedTab === "mitigation" ? "active": ""}`} onClick={() => setSelectedTab("mitigation")}>Mitigation</a>
                </li>: null
            }
            <li data-tab={"description"} className={`nav-item`}>
                <a className={`nav-link ${selectedTab === "description" ? "active": ""}`} onClick={() => setSelectedTab("description")}>Description</a>
            </li>
            { hasChaiFeature && shouldShow.chaiEnabled ? 
                <li data-tab={"description"} className={`nav-item`}>
                    <a className={`nav-link ${selectedTab === "chai" ? "active": ""}`} onClick={() => setSelectedTab("chai")}>AI</a>
                </li>: null
            }
            <li  data-tab={"affected-components"} className={`nav-item `}>
                <a className={`nav-link ${selectedTab === "affected components" ? "active": ""}`} onClick={() => setSelectedTab("affected components")}>Affected components</a>
            </li>
            { advice.links.length ? <li data-tab={"more-info"} className={`nav-item `}>
                <a className={`nav-link ${selectedTab === "more info" ? "active": ""}`} onClick={() => setSelectedTab("more info")}>More info</a>
            </li>: null}
            {shouldShow.actions && !shouldShow.onlyExclusion ? <li  data-tab={"actions"} className={`nav-item `}>
                <a className={`nav-link ${selectedTab === "actions" ? "active": ""}`} onClick={() => setSelectedTab("actions")}>Actions</a>
            </li>: null}
            {shouldShow.learnMore ? <li data-tab={"learn-more"} className={`nav-item `}>
                <a className={`nav-link ${selectedTab === "learn more" ? "active": ""}`} onClick={() => setSelectedTab("learn more")}>Learn more</a>
            </li>: null}
            {shouldShow.sysadminPanel ? <li data-tab={"sysadmin"} className={`nav-item `}>
                <a className={`nav-link ${selectedTab === "sysadmin" ? "active": ""}`} onClick={() => setSelectedTab("sysadmin")}>SA</a>
            </li>: null}
            <li className={`nav-item position-absolute threat threat-tab`}>
            <span className="nav-link severity"  >
						{advice.type && advice.type.toLowerCase() === "security" ?
							<>
								{advice.severity.toLowerCase() !== "trusted" && advice.severity.toLowerCase() !== "none" ?
									<span ><small>THREAT:</small> </span>
									:
									null
								}
								<strong className={severityClass}>
									{severity}
								</strong>
							</>
							:
							"SUGGESTION"
						}
					</span>
            </li>
        </ul>
    </div>
}

const ChaiPanel = ({display, advice}) => {
    const { uuid, branch } = useSelector((state) => state.projectInfo.report.project)
	// const [ messages, setMessages ] = useState([])
	const adviceData = {
		advice: {
			cve: advice.cve, cvss: advice.cvss, cwe: advice.cwe, epss: advice.epss, library: advice.library,
			severity: advice.severity, id: advice.id, description: advice.description
		},
		projectUuid: uuid,
		branch 
	}
    return  <div className={`${display ? "": "d-none"}`}>
        <Chai startChai={display} advice={adviceData}/>
    </div>


}
const Contents = ({selectedTab, advice, newMitigations, shouldShow, findDependencyInTree, removeMitigations}) => {
    return <div className='bg-white pt-2 pb-2'>
        <Description display={selectedTab==="description"} advice={advice}/>
        <Exclusion display={selectedTab==="mitigation"} advice={advice} shouldShow={shouldShow} removeMitigations={removeMitigations}/>
        <AffectedComponents display={selectedTab==="affected components"} advice={advice} components={advice.affectedComponents} findDependencyInTree={findDependencyInTree}/>
        <SysAdminPanel display={selectedTab==="sysadmin"} advice={advice}/>
        <MoreInfo display={selectedTab==="more info"} advice={advice}/>
        <Actions display={selectedTab==="actions"} shouldShow={shouldShow} advice={advice} newMitigations={newMitigations}/>
        <LearnMore display={selectedTab==="learn more"} advice={advice}/>
        <ChaiPanel display={selectedTab==="chai"} advice={advice}/>
    </div>
}
const ComponentCardBody = ({advice, shouldShow, newMitigations, findDependencyInTree, removeMitigations}) => {
    const [selectedTab, setSelectedTab] = useState(shouldShow.onlyExclusion ? "mitigation": "description")
    return <>
        <Tabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} shouldShow={shouldShow} advice={advice}></Tabs>
        <Contents selectedTab={selectedTab} advice={advice} shouldShow={shouldShow} newMitigations={newMitigations} findDependencyInTree={findDependencyInTree} removeMitigations={removeMitigations}></Contents>
    </>
}
const ComponentCard = ({ advice, shouldShow, newMitigations, findDependencyInTree, removeMitigations, jiraInfo}) => {
    const adviceUrl = advice.cve ? "https://nvd.nist.gov/vuln/detail/" + advice.cve : "/vulns?id=" + advice.id
    const vulnUrl = "/vulns?id="+advice.id
    return (
        <div className={`col-lg-12`} id="dependency-list" >
			<div className={"report-box dependency-inner" }>
				<div className={`report-box-header report-dependency ${shouldShow.onlyExclusion ? "gray": ""}`}>
					<div className="report-box-title">
                        <a data-link={"nvd"} className="advice-link mr-1 text-link pr-1 " href={adviceUrl} target='_blank' rel="noopener noreferer">
                            {advice.prominentIdentifier}
                        </a>
                        <a href={vulnUrl} data-link={"vulnerability"} target='_blank' rel="noopener noreferrer"><i className="fa-solid fa-link ml-1 text-white"></i></a>
					</div>
                    {advice.epss ? <span className='mr-2 badge badge-score'>EPSS: {Math.trunc(advice.epss * 10000) / 100}%</span>: ""}
                    {advice.cvss ? <span className='mr-2 badge badge-score'>CVSS: {advice.cvss}</span>: ""}
                    {advice.kevEntry ?
						<a className="text-link kev-link mr-2 mt-0 d-none-sm d-none-xs" href={"https://www.cisa.gov/known-exploited-vulnerabilities-catalog?search_api_fulltext="+advice.kevEntry.cve} rel="noopener noreferrer" target="_blank"> 
							<span className='badge badge-kev  badge-left'>KEV</span>
							{advice.kevEntry.exploited ?
								<span className='badge badge-kev badge-critical-inverted badge-right'>Exploited</span> 
								: 
								<span className='badge badge-kev badge-medium-inverted  badge-right'>Listed</span>
							}
						</a>
						: null
					}
                    {advice.jira ?
						<a className="text-link mr-3 " href={advice.jira.url} rel="noopener noreferrer" target="_blank">
							<span className='badge badge-score'>Jira JID-{advice.jira.id}</span>
						</a>
						: null
					}
				</div>
                <ComponentCardBody advice={advice} shouldShow={shouldShow} newMitigations={newMitigations} findDependencyInTree={findDependencyInTree} removeMitigations={removeMitigations}></ComponentCardBody>
			</div>
		</div>
    )
}

const ComponentsGroupedByAdvice = ({ chai, advisoriesMappedByCve, parentProps, filters, newMitigations, onlyExclusion=false, jiraInfo }) => {
    const {privateMode, isLatest, readonly, isSysAdmin, hasSecureCodeWarriorFeature, findDependencyInTree, removeMitigations} = parentProps;
    const hiddenLanguages = useSelector((state) => state.languagesFilter.toggledLanguages)
    const isAdviceExcluded = (advice, exclusions) => {
        if(!exclusions.length) return false
        if(!advice.affectedComponents.length) return true
        for(let i = 0; i < exclusions.length; i++) {
            if(exclusions[i].uuid === advice.id || exclusions[i].cve === advice.cve) return true
        }
        return false
    }
    const epssIsOutOfRange = (epss) => {
        const [min, max] = filters.epss
        if (!epss && (min === 0 && max === 100)) {
            return false;
        } else if (!epss && (min !== 0 || max !== 100)){
            return true
        } else if (epss * 100 >= min && epss * 100 <= max) {
            return false
        } else {
            return true
        }
    }
    const cvssIsOutOfRange = (cvss) => {
        const [min, max] = filters.cvss
        if (!cvss && (min === 0 && max === 10)) {
            return false;
        } else if (!cvss && (min !== 0 || max !== 10)){
            return true
        } else if (cvss >= min && cvss <= max) {
            return false
        } else {
            return true
        }
    }
    return <div className='advisory-grouped-cve w-100'>
        { Object.keys(advisoriesMappedByCve).map((cve, i) => {
            const advice = advisoriesMappedByCve[cve]
            let severity = advice.severity.toLowerCase()
            if( !(severity in filters.severity) ) severity = "others"
            console.log({severity, f: filters.severity})
            const shouldShow={
                actions: privateMode && !readonly && isLatest,
                sysadminPanel: isSysAdmin,
                globalExclusion: {
                    advice: false,
                    cve: false,
                    library: false
                },
                onlyExclusion,
                learnMore: hasSecureCodeWarriorFeature && advice.cve,
                chaiEnabled: chai.enabled
            }
            if(hiddenLanguages.includes(advice.library.language) && advice.affectedComponents.length === 1) return null
            if(!onlyExclusion && isAdviceExcluded(advice, advice.exclusions)) return null
            if(onlyExclusion && !isAdviceExcluded(advice, advice.exclusions)) return null
            return <div key={`${i}-advice-${advice.id}`} data-cve={advice.prominentIdentifier} className={`${!cvssIsOutOfRange(advice.cvss) && !epssIsOutOfRange(advice.epss) && filters.severity[severity] ? "d-flex": "d-none"} w-100`}>
                <ComponentCard  advice={advice} shouldShow={shouldShow} newMitigations={newMitigations} findDependencyInTree={findDependencyInTree} removeMitigations={removeMitigations}/>
            </div>
        })}
    </div>
}

export const ExcludedComponentsGroupedByAdvice = (props) => {
    return <ComponentsGroupedByAdvice {...props} onlyExclusion={true}/>
}

export default ComponentsGroupedByAdvice